import { useMemo, useEffect } from 'react'

import { debounce } from '../../utils'

import { usePrinter } from '../../hooks/usePrinter'
import useIsMobile from '../../hooks/useIsMobile'

import PrintDownloadAlertView from './PrintDownloadAlert/View'
import PrintAppNotInstalledAlertView from './PrintAlert/PrintAppNotInstalledAlertView'
import PrinterNotConfiguredAlertView from './PrintAlert/PrinterNotConfiguredAlertView'

import CancelShippingAlertView from './CancelShippingAlert/View'
import CancelPrintAlert from './CancelPrintAlert/View'

import {
  useAppAlertsStatusContext,
  useAppAlertsActionsContext,
} from './AppAlertsProvider'
import NewManualAlertView from './NewManualAlert/View'
import ImportantAlertView from './ImportantAlert/View'

export const INACTIVE_SESSION_COUNTDOWN = 1000 * 60 * 5
export const TIME_SHOWING_INACTIVE_SESSION_MODAL = 1000 * 30

const HIDE_INIT_PRINT_ALERT_KEY = 'hideInitPrintAlert'
const writeHideInitPrintAlertPreference = newValue =>
  localStorage.setItem(HIDE_INIT_PRINT_ALERT_KEY, String(newValue))
const readHideInitPrintAlertPreference = () => {
  {
    // REVIEW Preference migration. Once the new value is served to all clients, this block can be safely deleted.
    const legacyKey = 'displayInitPrintAlert'
    const legacyRawValue = localStorage.getItem(legacyKey)
    if (legacyRawValue !== null) {
      localStorage.removeItem(legacyKey)

      // NOTE legacy value was inversely named
      const migratedValue = legacyRawValue === 'true'
      writeHideInitPrintAlertPreference(migratedValue)
      return migratedValue
    }
  }

  const rawValue = localStorage.getItem(HIDE_INIT_PRINT_ALERT_KEY)
  if (rawValue === null) {
    writeHideInitPrintAlertPreference(false)
    return false
  }

  return rawValue === 'true'
}

const AppAlerts = () => {
  const isMobile = useIsMobile()
  const { isWorking: isPrinterWorking } = usePrinter()

  const {
    showPrintDownloadAlert,
    showPrintAppNotInstalledAlert,
    showPrinterNotConfiguredAlert,
    showCancelShippingsAlert,
    showCancelPrintAlert,
    showNewUserManualAlert,
    showImportantAlert
  } = useAppAlertsStatusContext()
  const {
    setShowPrintDownloadAlert,
    setShowPrintAppNotInstalledAlert,
    setShowPrinterNotConfiguredAlert,
    setShowNewUserManualAlert,
    setShowImportantAlert
  } = useAppAlertsActionsContext()

  const debouncedSetShowPrintDownloadAlert = useMemo(
    () => debounce(setShowPrintDownloadAlert, 1000),
    [setShowPrintDownloadAlert],
  )

  useEffect(() => {
    const isInitPrintAlertDisabled = readHideInitPrintAlertPreference()

    if (!isInitPrintAlertDisabled) {
      debouncedSetShowPrintDownloadAlert(!isPrinterWorking)
    }
  }, [debouncedSetShowPrintDownloadAlert, isPrinterWorking])

  const handleHidePrintDownloadAlert = () => {
    setShowPrintDownloadAlert(false)
    writeHideInitPrintAlertPreference(true)
  }

  if (isMobile) return null

  if (showImportantAlert) {
    return <ImportantAlertView onClose={()=>setShowImportantAlert(null)}/>
  }

  if (showNewUserManualAlert) {
    return <NewManualAlertView onClose={()=>setShowNewUserManualAlert(null)}/>
  }

  if (showCancelShippingsAlert) {
    return <CancelShippingAlertView />
  }

  if (showCancelPrintAlert) {
    return <CancelPrintAlert />
  }

  if (showPrintAppNotInstalledAlert) {
    return (
      <PrintAppNotInstalledAlertView
        closeAlert={() => {
          setShowPrintAppNotInstalledAlert(false)
          setShowPrintDownloadAlert(false)
        }}
      />
    )
  }

  if (showPrinterNotConfiguredAlert) {
    return (
      <PrinterNotConfiguredAlertView
        closeAlert={() => {
          setShowPrinterNotConfiguredAlert(false)
        }}
      />
    )
  }

  if (showPrintDownloadAlert) {
    return <PrintDownloadAlertView closeAlert={handleHidePrintDownloadAlert} />
  }

  return null
}

export default AppAlerts
